import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import ReplyIcon from '@mui/icons-material/Reply';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import MessageBubble from './MessageBubble';
import UserAvatar from './UserAvatar';
import EmojiPickerButton from './emoji/EmojiPickerButton';
import EmojiSuggestionList from './emoji/EmojiSuggestionList';
import {
  searchEmojis,
  findEmojiByShortcode,
  matchShortcodeQuery,
  matchCompletedShortcode,
} from './emoji/emojiSearch';

const readFileAsBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const imageDimensions = dataUrl =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({});
    img.src = dataUrl;
  });

// Groups consecutive messages by calendar day for date separators.
const dayLabel = date => {
  const d = dayjs(date);
  const now = dayjs();
  if (now.isSame(d, 'day')) return 'Heute';
  if (now.subtract(1, 'day').isSame(d, 'day')) return 'Gestern';
  return d.format('dddd, DD.MM.YYYY');
};

export default function ChatWindow({
  conversation,
  messages,
  hasMoreOlder,
  isLoadingOlder,
  onLoadOlder,
  currentUserId,
  typing,
  onSend,
  onEditMessage,
  onDeleteMessage,
  onReact,
  onUpdateState,
  onUpload,
  onTyping,
  canModerate,
}) {
  const [input, setInput] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pending, setPending] = useState([]); // uploaded-but-not-sent attachments
  const [uploading, setUploading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [emojiSuggestions, setEmojiSuggestions] = useState([]);
  const [emojiSelectedIndex, setEmojiSelectedIndex] = useState(0);

  const scrollRef = useRef(null);
  const preserveRef = useRef(null);
  const atBottomRef = useRef(true);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const typingStateRef = useRef(false);
  const typingTimerRef = useRef(null);
  const composerRef = useRef(null);
  // Active ":shortcode" fragment before the caret ({ query, start, end }).
  const emojiQueryRef = useRef(null);
  // Monotonic sequence so stale async search results never overwrite newer ones.
  const emojiSearchSeqRef = useRef(0);

  const conversationId = conversation?.id;
  const otherName = conversation?.otherUser?.displayName || 'Mitarbeiter';
  const otherLastReadAt = conversation?.otherLastReadAt || null;

  const closeEmojiSuggestions = () => {
    emojiQueryRef.current = null;
    emojiSearchSeqRef.current += 1;
    setEmojiSuggestions([]);
    setEmojiSelectedIndex(0);
  };

  // Reset input/scroll intent when switching conversations.
  useEffect(() => {
    setInput('');
    setReplyTo(null);
    setEditing(null);
    setPending([]);
    atBottomRef.current = true;
    preserveRef.current = null;
    emojiQueryRef.current = null;
    emojiSearchSeqRef.current += 1;
    setEmojiSuggestions([]);
    setEmojiSelectedIndex(0);
  }, [conversationId]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.scrollTop < 60 && hasMoreOlder && !isLoadingOlder) {
      preserveRef.current = { prevHeight: el.scrollHeight };
      onLoadOlder();
    }
  };

  // Keep the viewport anchored: preserve position when prepending older
  // messages, otherwise stick to the bottom for new ones.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (preserveRef.current) {
      el.scrollTop = el.scrollHeight - preserveRef.current.prevHeight;
      preserveRef.current = null;
    } else if (atBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const emitTyping = value => {
    if (typingStateRef.current === value) return;
    typingStateRef.current = value;
    onTyping(value);
  };

  // Restores focus and caret position after a programmatic input change.
  const setCaret = position => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(position, position);
      }
    });
  };

  // Inserts text (an emoji from the picker) at the current caret position.
  const insertAtCaret = text => {
    const el = inputRef.current;
    const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : input.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
    setInput(prev => prev.slice(0, start) + text + prev.slice(end));
    setCaret(start + text.length);
  };

  // Replaces the active ":fragment" with the picked emoji suggestion.
  const applyEmojiSuggestion = suggestion => {
    const ctx = emojiQueryRef.current;
    if (!ctx || !suggestion) return;
    const inserted = `${suggestion.native} `;
    setInput(prev => prev.slice(0, ctx.start) + inserted + prev.slice(ctx.end));
    closeEmojiSuggestions();
    setCaret(ctx.start + inserted.length);
  };

  const handleInputChange = e => {
    const value = e.target.value;
    const caret = typeof e.target.selectionStart === 'number' ? e.target.selectionStart : value.length;
    setInput(value);
    emitTyping(true);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => emitTyping(false), 2500);

    // A closing colon completes a shortcode (":cloud:") — replace it with the
    // emoji once the (lazily loaded) index resolves it. Only applied if the
    // text has not changed in the meantime.
    const completed = matchCompletedShortcode(value, caret);
    if (completed) {
      closeEmojiSuggestions();
      const seq = ++emojiSearchSeqRef.current;
      findEmojiByShortcode(completed.shortcode).then(emoji => {
        if (!emoji || seq !== emojiSearchSeqRef.current) return;
        setInput(prev => {
          if (prev !== value) return prev;
          setCaret(completed.start + emoji.native.length + 1);
          return `${prev.slice(0, completed.start)}${emoji.native} ${prev.slice(completed.end)}`;
        });
      });
      return;
    }

    // An in-progress ":fragment" drives the Slack-style suggestion popup.
    const fragment = matchShortcodeQuery(value, caret);
    if (fragment) {
      emojiQueryRef.current = fragment;
      const seq = ++emojiSearchSeqRef.current;
      searchEmojis(fragment.query).then(results => {
        if (seq !== emojiSearchSeqRef.current) return;
        setEmojiSuggestions(results);
        setEmojiSelectedIndex(0);
      });
    } else if (emojiQueryRef.current) {
      closeEmojiSuggestions();
    }
  };

  useEffect(() => () => typingTimerRef.current && clearTimeout(typingTimerRef.current), []);

  const handleFiles = async e => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = '';
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const dataUrl = await readFileAsBase64(file);
        const isImage = file.type.startsWith('image/');
        const dims = isImage ? await imageDimensions(dataUrl) : {};
        const res = await onUpload({
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          dataBase64: dataUrl,
          width: dims.width,
          height: dims.height,
        });
        if (res && res.attachment) setPending(prev => [...prev, res.attachment]);
      }
    } catch (_e) {
      // errors surfaced via toast in the hook
    } finally {
      setUploading(false);
    }
  };

  const removePending = attachmentId =>
    setPending(prev => prev.filter(a => a.attachmentId !== attachmentId));

  const canSend = (input.trim().length > 0 || pending.length > 0) && !uploading;

  const submit = async () => {
    closeEmojiSuggestions();
    if (editing) {
      const text = input.trim();
      if (!text) return;
      await onEditMessage(editing.id, text);
      setEditing(null);
      setInput('');
      return;
    }
    if (!canSend) return;
    const payload = {
      text: input.trim() || undefined,
      replyToMessageId: replyTo?.id,
      attachmentIds: pending.map(a => a.attachmentId),
    };
    setInput('');
    setReplyTo(null);
    setPending([]);
    emitTyping(false);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    atBottomRef.current = true;
    await onSend(payload);
  };

  const handleKeyDown = e => {
    // While the emoji autocomplete is open its navigation takes precedence
    // (Enter picks the suggestion instead of sending).
    if (emojiSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setEmojiSelectedIndex(i => (i + 1) % emojiSuggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setEmojiSelectedIndex(i => (i - 1 + emojiSuggestions.length) % emojiSuggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        applyEmojiSuggestion(emojiSuggestions[emojiSelectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeEmojiSuggestions();
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      setEditing(null);
      setReplyTo(null);
    }
  };

  const beginEdit = message => {
    setEditing(message);
    setReplyTo(null);
    setInput(message.text || '');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const beginReply = message => {
    setReplyTo(message);
    setEditing(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // The other participant's read cursor: every own message created at or
  // before this moment has been seen and gets the double tick — not just the
  // newest one (mirrors WhatsApp/Slack semantics).
  const otherReadTime = useMemo(
    () => (otherLastReadAt ? dayjs(otherLastReadAt).valueOf() : null),
    [otherLastReadAt]
  );
  const isSeenByOther = useCallback(
    message =>
      otherReadTime !== null &&
      String(message.senderId) === String(currentUserId) &&
      dayjs(message.createdAt).valueOf() <= otherReadTime,
    [otherReadTime, currentUserId]
  );

  const rows = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const m of messages) {
      const d = dayjs(m.createdAt).format('YYYY-MM-DD');
      if (d !== lastDay) {
        out.push({ type: 'day', key: `day-${d}`, label: dayLabel(m.createdAt) });
        lastDay = d;
      }
      out.push({ type: 'msg', key: m.id, message: m });
    }
    return out;
  }, [messages]);

  const setState = patch => {
    setMenuAnchor(null);
    onUpdateState(patch);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <UserAvatar name={otherName} size={40} online={!!conversation?.otherUser?.isOnline} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600 }} noWrap>
            {otherName}
          </Typography>
          <Typography variant='caption' color={typing ? 'primary.main' : 'text.secondary'}>
            {typing
              ? 'schreibt …'
              : conversation?.otherUser?.isOnline
                ? 'Online'
                : 'Offline'}
          </Typography>
        </Box>
        {conversation?.muted && (
          <Tooltip title='Stummgeschaltet'>
            <NotificationsOffOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
          </Tooltip>
        )}
        <IconButton onClick={e => setMenuAnchor(e.currentTarget)}>
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => setState({ muted: !conversation?.muted })}>
            {conversation?.muted ? 'Stummschaltung aufheben' : 'Stummschalten'}
          </MenuItem>
          <MenuItem onClick={() => setState({ pinned: !conversation?.pinned })}>
            {conversation?.pinned ? 'Nicht mehr anpinnen' : 'Anpinnen'}
          </MenuItem>
          <MenuItem onClick={() => setState({ archived: !conversation?.archived })}>
            {conversation?.archived ? 'Aus Archiv holen' : 'Archivieren'}
          </MenuItem>
          <MenuItem onClick={() => setState({ cleared: true })} sx={{ color: 'error.main' }}>
            Verlauf für mich löschen
          </MenuItem>
        </Menu>
      </Box>

      {/* Messages */}
      <Box ref={scrollRef} onScroll={handleScroll} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
        {isLoadingOlder && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        )}
        {rows.length === 0 && (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
            <Typography variant='body2'>Noch keine Nachrichten. Sag Hallo! 👋</Typography>
          </Box>
        )}
        {rows.map(row =>
          row.type === 'day' ? (
            <Divider key={row.key} sx={{ my: 1.5 }}>
              <Typography variant='caption' color='text.secondary'>
                {row.label}
              </Typography>
            </Divider>
          ) : (
            <MessageBubble
              key={row.key}
              message={row.message}
              isMine={String(row.message.senderId) === String(currentUserId)}
              seen={isSeenByOther(row.message)}
              onReply={beginReply}
              onReact={onReact}
              onEdit={beginEdit}
              onDelete={onDeleteMessage}
              canModerate={canModerate}
            />
          )
        )}
      </Box>

      {/* Composer */}
      <Box
        ref={composerRef}
        sx={{ px: 2, py: 1.5, borderTop: theme => `1px solid ${theme.palette.divider}` }}
      >
        <EmojiSuggestionList
          anchorEl={emojiSuggestions.length > 0 ? composerRef.current : null}
          suggestions={emojiSuggestions}
          selectedIndex={emojiSelectedIndex}
          onHoverIndex={setEmojiSelectedIndex}
          onPick={applyEmojiSuggestion}
        />
        {(replyTo || editing) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: theme =>
                theme.palette.mode === 'light' ? theme.palette.grey[100] : 'rgba(255,255,255,0.06)',
            }}
          >
            <ReplyIcon fontSize='small' color='primary' />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant='caption' color='primary' sx={{ fontWeight: 600, display: 'block' }}>
                {editing ? 'Nachricht bearbeiten' : 'Antworten'}
              </Typography>
              <Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
                {(editing || replyTo)?.text || (replyTo?.attachments?.length ? 'Anhang' : '')}
              </Typography>
            </Box>
            <IconButton
              size='small'
              onClick={() => {
                setReplyTo(null);
                setEditing(null);
                setInput('');
              }}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        )}

        {pending.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            {pending.map(att => (
              <Paper
                key={att.attachmentId}
                variant='outlined'
                sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <Typography variant='caption' noWrap sx={{ maxWidth: 140 }}>
                  {att.kind === 'image' ? '🖼️ ' : '📎 '}
                  {att.name}
                </Typography>
                <IconButton size='small' onClick={() => removePending(att.attachmentId)}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Paper>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <input
            ref={fileRef}
            type='file'
            multiple
            hidden
            onChange={handleFiles}
            data-testid='chat-file-input'
          />
          <Tooltip title='Datei anhängen'>
            <span>
              <IconButton onClick={() => fileRef.current?.click()} disabled={uploading || !!editing}>
                {uploading ? <CircularProgress size={20} /> : <AttachFileIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            inputRef={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={editing ? 'Nachricht bearbeiten …' : `Nachricht an ${otherName} …`}
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            size='small'
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <EmojiPickerButton onSelect={insertAtCaret} />
                  <IconButton
                    color='primary'
                    onClick={submit}
                    disabled={editing ? !input.trim() : !canSend}
                  >
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

ChatWindow.propTypes = {
  conversation: PropTypes.object,
  messages: PropTypes.array,
  hasMoreOlder: PropTypes.bool,
  isLoadingOlder: PropTypes.bool,
  onLoadOlder: PropTypes.func.isRequired,
  currentUserId: PropTypes.string,
  typing: PropTypes.bool,
  onSend: PropTypes.func.isRequired,
  onEditMessage: PropTypes.func.isRequired,
  onDeleteMessage: PropTypes.func.isRequired,
  onReact: PropTypes.func.isRequired,
  onUpdateState: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onTyping: PropTypes.func.isRequired,
  canModerate: PropTypes.bool,
};
